import { useTranslation } from "react-i18next";
import { DataTableSkeleton, InlineNotification } from "@carbon/react";
import { useManagerDashboard } from "../../../hooks/useManagerDashboard";
import PulseBar from "./PulseBar";
import NeedsYouList from "./NeedsYouList";
import TeamAttentionList from "./TeamAttentionList";
import { actionCopy } from "./actionCopy";
import "./managerDashboard.scss";

/**
 * The manager's home page: counters, then their own blocked work, then
 * their team's. No analytics block, no date-range picker — every row here
 * is something the backend decided is worth a click, in the order the
 * backend decided to show it. This component must never sort, reverse, or
 * re-group what `useManagerDashboard` returns; the ranking is the product.
 */
const ManagerDashboard = () => {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useManagerDashboard();

  if (isLoading) {
    return (
      <div className="manager-dashboard">
        <DataTableSkeleton
          showHeader={false}
          showToolbar={false}
          rowCount={5}
          columnCount={3}
        />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="manager-dashboard">
        <InlineNotification
          kind="error"
          lowContrast
          title={t("managerDashboard.loadFailed")}
          hideCloseButton
        />
      </div>
    );
  }

  return (
    <div className="manager-dashboard">
      <h1 className="manager-dashboard__title">
        {t("managerDashboard.title")}
      </h1>

      {data.degraded.length > 0 ? (
        <InlineNotification
          kind="warning"
          lowContrast
          title={t("managerDashboard.degraded")}
          subtitle={data.degraded
            .map((type) => t(actionCopy[type].labelKey))
            .join(", ")}
          hideCloseButton
        />
      ) : null}

      <PulseBar counts={data.pulse} />
      <NeedsYouList items={data.needsYou} />
      <TeamAttentionList
        items={data.teamAttention.items}
        total={data.teamAttention.total}
      />
    </div>
  );
};

export default ManagerDashboard;
